/**
 * The top of the virtual account page (TMMAPP-0108; the v2 panel, TMMAPP-0151 §F 18, prototype
 * P:546–565): the selected panel the list page shows, with this page's actions on it.
 *
 * - The visibility line (served names) and the standing agreement (`VirtualAccountPanel`).
 * - The envelope grid (`minmax(240px)`, gap 12): the unassigned pool first — its card opens the
 *   Ledger filtered to Unassigned and its kebab holds Move money, Split Unassigned (writers) and the
 *   Unassigned register — then each active envelope's v2 card (`EnvelopeTile`) with its kebab and,
 *   for a writer, its reorder handle, then the add card (writers). With no envelope, the empty card.
 * - A footer: the served sentence "{Unassigned} unassigned · {in envelopes} across N envelopes[ and
 *   {x} archived]" (every figure served, never added up here), Move money (writers), and the
 *   archived link — always shown — that opens the Archived envelopes dialog (C6).
 *
 * The headline balance sits in the page header.
 */
import type { ReactNode } from 'react';
import { Box, Link, MenuItem, Stack, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import type { VirtualAccountDetail } from '@/api/virtualAccounts';
import type { Envelope } from '@/api/envelopes';
import { SortableCardGrid, type ReorderHandleProps } from '@/components/SortableCardGrid';
import { formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { OverflowMenu } from '@/shell/OverflowMenu';
import { EnvelopeTile } from './EnvelopeTile';
import { unassignedTileContent } from './envelopeTileContent';
import { VirtualAccountPanel } from './VirtualAccountPanel';

/** The envelope cards' minimum width inside the panel (prototype P:552). */
export const ENVELOPE_MIN_CARD_WIDTH = 240;

interface BalanceCardProps {
  detail: VirtualAccountDetail;
  /** Active, non-system envelopes in display order. */
  envelopes: Envelope[];
  archivedCount: number;
  canWrite: boolean;
  /** The served visibility line; null renders none (the list row has not arrived). */
  visibility: string | null;
  agreement: string | null;
  renderCard: (envelope: Envelope, reorder: ReorderHandleProps | null) => ReactNode;
  onReorder: (ids: Array<string | number>) => Promise<void>;
  /** The dashed add card (writers only); null hides it. */
  addCard: ReactNode;
  onShowUnassigned: () => void;
  onMoveMoney: () => void;
  onSplitUnassigned: () => void;
  onUnassignedRegister: () => void;
  onOpenArchived: () => void;
}

export function BalanceCard({
  detail,
  envelopes,
  archivedCount,
  canWrite,
  visibility,
  agreement,
  renderCard,
  onReorder,
  addCard,
  onShowUnassigned,
  onMoveMoney,
  onSplitUnassigned,
  onUnassignedRegister,
  onOpenArchived,
}: BalanceCardProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const money = (minor: number) => formatMoneyMinor(minor, detail.currency, locale);
  // One whole sentence per case, so each locale orders and agrees it (never a fragment appended).
  const withArchived = detail.archivedEnvelopesMinor !== 0;
  const subtitle = t(
    withArchived ? 'virtualAccount.balance.subtitleArchived' : 'virtualAccount.balance.subtitle',
    {
      unassigned: money(detail.unassignedMinor),
      envelopes: money(detail.envelopesMinor),
      archived: money(detail.archivedEnvelopesMinor),
      count: envelopes.length,
    },
  );

  const unassignedMenu = (
    <OverflowMenu
      variant="card"
      title={t('virtualAccount.unassigned')}
      label={t('virtualAccount.balance.unassignedActions')}
    >
      {(close) => {
        const pick = (action: () => void) => () => {
          close();
          action();
        };
        return [
          canWrite && (
            <MenuItem key="move" onClick={pick(onMoveMoney)}>
              {t('virtualAccount.balance.moveMoneyItem')}
            </MenuItem>
          ),
          canWrite && (
            <MenuItem key="split" onClick={pick(onSplitUnassigned)}>
              {t('virtualAccount.menu.splitUnassigned')}
            </MenuItem>
          ),
          <MenuItem key="register" onClick={pick(onUnassignedRegister)}>
            {t('virtualAccount.unassignedRegister.title')}
          </MenuItem>,
        ];
      }}
    </OverflowMenu>
  );

  return (
    <Box data-balance-card="">
      <VirtualAccountPanel
        visibility={visibility}
        agreement={agreement}
        aria-label={t('virtualAccount.envelopes.title')}
      >
        {/* The envelope cards' h3s sit under an h2 (the page has its h1; the panel draws no heading). */}
        <Typography component="h2" sx={visuallyHidden}>
          {t('virtualAccount.envelopes.title')}
        </Typography>
        <SortableCardGrid
          items={envelopes}
          getId={(envelope) => envelope.id}
          getName={(envelope) => envelope.name}
          canReorder={canWrite}
          onReorder={onReorder}
          renderCard={renderCard}
          minCardWidth={ENVELOPE_MIN_CARD_WIDTH}
          sx={{ gap: 1.5 }}
          leading={
            <EnvelopeTile
              heading={false}
              content={unassignedTileContent(detail.unassignedMinor, t)}
              onOpen={onShowUnassigned}
              openLabel={t('virtualAccount.balance.unassignedShow', {
                amount: money(detail.unassignedMinor),
              })}
              currency={detail.currency}
              menu={unassignedMenu}
            />
          }
          {...(addCard !== null ? { trailing: addCard } : {})}
        />
        {envelopes.length === 0 && (
          <Box data-envelopes-empty="" sx={{ maxWidth: 520 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              {t('virtualAccount.envelopes.empty')}
            </Typography>
          </Box>
        )}
        <Stack direction="row" sx={{ flexWrap: 'wrap', alignItems: 'baseline', gap: '6px 16px' }}>
          <Typography
            variant="body2"
            component="p"
            data-balance-subtitle=""
            sx={{ fontVariantNumeric: 'tabular-nums', flex: '1 1 260px' }}
          >
            {subtitle}
          </Typography>
          {canWrite && (
            <Link component="button" type="button" variant="body2" onClick={onMoveMoney}>
              {t('virtualAccount.balance.moveMoney')}
            </Link>
          )}
          <Link
            component="button"
            type="button"
            onClick={onOpenArchived}
            underline="always"
            sx={{ fontSize: 12, fontWeight: 500 }}
          >
            {t('virtualAccount.envelopes.archivedLink', { count: archivedCount })}
          </Link>
        </Stack>
      </VirtualAccountPanel>
    </Box>
  );
}
